import {
  getSessionData,
  getVersionedCallbackUrl,
  redirectToWebform,
} from "@repo/shared-utils/common-utils";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import { APP_PROMPTS } from "@repo/shared-utils/pwa.constants";
import { SSORedirectHandler } from "../../../../apps/trading-mall-app/app/components/ClickHandler/RedirectionConfig.interface";
import {
  getApplicationIdAndProdCode,
  type IPlcsInsertApiRequest,
  type IPlcsInsertApiResponse,
  type ISalplInsertApiRequest,
  type ISalplInsertApiResponse,
  postPlcsInsert,
  postSalplInsert,
  redirectionInAppWebform,
  renderProduct,
  setFormData,
} from "./utils";
import { offerAPI } from "../../../api-client/src/services/icors.service";
import { getUserDetails } from "../services/demogApi";
import { getOrDefaultValue } from "@repo/shared-utils/insert-params";

type IPlInsertPayload = {
  mobileNo: string;
  journeyName: string;
  loginAmt: string;
  requestedLoanAmt: string;
  loanVariant: string;
  requestedLoanTenor: string;
  callbackRequested: boolean;
  requestedIRR: string;
  requestedROI: string;
  pqOfferIrr: string;
  emiAmt: string;
  offerId?: string;
  offerProduct?: string;
  sourcingBranchId?: string;
  appId?: string;
  id: string;
  loanType: string;
  hybridTenor?: string;
  dropLineTenor?: string;
  hybridEMIAmt?: string;
  droplineEMIAmt?: string;
  nativeVersion?: string;
  appDtls?: IPlcsInsertApiRequest["appDtls"];
};

export const nullCheck = (value: string | undefined) => (value ? value : "");

export const plHandler: SSORedirectHandler = async (data) => {
  // Preserve webform start page for "Go back and edit" behavior.
  sessionStorage.setItem("plWebformJourneyStartedPage", window.location.pathname);

  sessionStorage.setItem(
    "prefillData",
    JSON.stringify({
      amount: data.data?.genericInsert?.amount ?? null,
      initialTenure: data.data?.genericInsert?.initialTenure ?? null,
      variant: data.data?.genericInsert?.variant ?? null,
    })
  );

  const { mobileNo } = await getSessionData();
  const [applicationID, prodCode] = await getApplicationIdAndProdCode(data, ["SALPL", "PLCS"]);
  const resume = Boolean(applicationID);

  const offerData = await offerAPI("salpl,plcs");
  const selectedOffer = offerData?.at(0);
  const userData = await getUserDetails(mobileNo);
  const custType = getOrDefaultValue(userData?.data?.custType, "NTB");
  const product =
    renderProduct(prodCode, selectedOffer?.icorsProduct)?.toUpperCase() === "PLCS"
      ? "PLCS"
      : "SALPL";

  const genericInsert = data.data?.genericInsert ?? {};
  let basePayload: IPlInsertPayload = {
    mobileNo,
    journeyName: selectedOffer?.journeyName ?? "SAL_NO_OFFER",
    loginAmt: String(genericInsert.amount ?? ""),
    requestedLoanAmt: String(selectedOffer?.icorsAmount ?? genericInsert.amount ?? ""),
    loanVariant: String(genericInsert.variant ?? ""),
    requestedLoanTenor: String(genericInsert.initialTenure ?? ""),
    callbackRequested: false,
    requestedIRR: String(selectedOffer?.offerRoi ?? genericInsert.roi ?? "0"),
    requestedROI: String(selectedOffer?.offerRoi ?? "0"),
    pqOfferIrr: String(selectedOffer?.pqOfferIrr ?? genericInsert.roi ?? "0"),
    emiAmt: String(genericInsert.initialEmi ?? ""),
    offerId: selectedOffer?.offerId,
    offerProduct: selectedOffer?.offerProduct ?? "SPL",
    sourcingBranchId: selectedOffer?.sourcingBranchId,
    appId: applicationID ?? "",
    id: "",
    loanType: nullCheck(genericInsert.loanType) || "New",
    nativeVersion: isOpenInApp()
      ? String(window.tarsier?.invokePrompt?.(APP_PROMPTS.APP_VERSION) ?? "")
      : "",
    appDtls: [
      {
        pan: nullCheck(userData?.data?.panNo),
        dob: nullCheck(userData?.data?.dob).split("-").reverse().join("/"),
        employmentType: nullCheck((await offerAPI("SALPL,PLCS"))?.[0]?.employmentType),
        gender: nullCheck(userData?.data?.gender),
        fullName: [userData?.data?.firstName, userData?.data?.lastName]
          .map((v) => nullCheck(v))
          .filter(Boolean)
          .join(" "),
        kycDtls: {
          address_pin: nullCheck(userData?.data?.pinCode),
          address_city: nullCheck(userData?.data?.city),
        },
        gstInNo: nullCheck(userData?.data?.gstnNumber),
      },
    ],
  };

  if (/hybrid/i.test(String(genericInsert.variant ?? ""))) {
    basePayload = {
      ...basePayload,
      hybridTenor: String(
        +(genericInsert.initialTenure ?? 0) - +(genericInsert.subsqTenure ?? 0) || ""
      ),
      dropLineTenor: String(genericInsert.subsqTenure ?? ""),
      hybridEMIAmt: String(genericInsert.initialEmi ?? ""),
      droplineEMIAmt: String(genericInsert.subsqEmi ?? ""),
    };
  }

  let plcsResData: IPlcsInsertApiResponse | undefined;
  let salplResData: ISalplInsertApiResponse | undefined;

  if (product === "PLCS") {
    const plcsPayload: IPlcsInsertApiRequest = {
      ...basePayload,
      startNewApplication: true,
    };
    const plcsResponse = await postPlcsInsert(plcsPayload);
    plcsResData = plcsResponse;
  } else {
    const salplPayload: ISalplInsertApiRequest = basePayload;
    const salplResponse = await postSalplInsert(salplPayload);
    salplResData = salplResponse;
  }

  const callbackUrl = await getVersionedCallbackUrl(data.mapping);
  const inAppLink = product === "PLCS" ? "b2c/plcs" : "b2c/salpl";
  const formData = setFormData({
    custType,
    applicationID,
    product,
    prodCode,
    plcsResData,
    salplResData,
    resume,
  });

  if (isOpenInApp()) {
    await redirectionInAppWebform(
      String(formData.applicationID || ""),
      product,
      undefined,
      Boolean(formData.resume)
    );
    return;
  }

  redirectToWebform(callbackUrl.toString(), formData, inAppLink);
};
